import { CbhIcon, type CbhIconProps, Text } from "@clipboard-health/ui-components";
import { Box } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { INTERVIEW_TYPE } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";

import { type PlacementWithDetails } from "../../../api/useFetchPaginatedPlacements";

interface InterviewTypeCalloutCardProps {
  placement: PlacementWithDetails;
}

type InterviewVariant = "in-person" | "phone" | "apply-web" | "apply-clipboard";

function getInterviewVariant(placement: PlacementWithDetails): InterviewVariant {
  const { scheduleInterviews, selfServeInterviewBookingType } =
    placement.interviewSettings?.attributes ?? {};

  if (scheduleInterviews) {
    if (selfServeInterviewBookingType === INTERVIEW_TYPE.IN_PERSON) {
      return "in-person";
    }

    if (selfServeInterviewBookingType === INTERVIEW_TYPE.PHONE_SCREEN) {
      return "phone";
    }
  }

  if (placement.workplace.placementSettings?.isProspect) {
    return "apply-web";
  }

  if (!scheduleInterviews && placement.webApplicationLink) {
    return "apply-web";
  }

  return "apply-clipboard";
}

interface VariantConfig {
  iconType: CbhIconProps["type"];
  title: string;
  description: string;
  backgroundColor: (theme: Theme) => string | undefined;
  borderColor: (theme: Theme) => string | undefined;
  iconBackground: (theme: Theme) => string | undefined;
  color: (theme: Theme) => string | undefined;
}

const VARIANT_CONFIGS: Record<InterviewVariant, VariantConfig> = {
  "in-person": {
    iconType: "user",
    title: "In-person interview",
    description: "You'll interview with this workplace in person at your scheduled time.",
    backgroundColor: (theme) => theme.palette.intent?.success.background,
    borderColor: (theme) => theme.palette.intent?.success.border,
    iconBackground: (theme) => theme.palette.intent?.success.border,
    color: (theme) => theme.palette.intent?.success.text,
  },
  phone: {
    iconType: "phone",
    title: "Phone call interview",
    description: "This workplace will call you to interview over the phone at your scheduled time.",
    backgroundColor: (theme) => theme.palette.placement?.apply.feature.background,
    borderColor: (theme) => theme.palette.shift?.bundle.border,
    iconBackground: (theme) => theme.palette.shift?.bundle.border,
    color: (theme) => theme.palette.shift?.block.text,
  },
  "apply-web": {
    iconType: "pencil",
    title: "Apply through company website",
    description: "You're applying online. The workplace will reach out about next steps.",
    backgroundColor: (theme) => theme.palette.shift?.priority.background,
    borderColor: (theme) => theme.palette.placement?.feature.background,
    iconBackground: (theme) => theme.palette.placement?.feature.background,
    color: (theme) => theme.palette.shift?.priority.text,
  },
  "apply-clipboard": {
    iconType: "email-filled",
    title: "Apply through Clipboard",
    description:
      "You're applying through Clipboard. The workplace will reach out about next steps.",
    backgroundColor: (theme) => theme.palette.intent?.info.background,
    borderColor: (theme) => theme.palette.intent?.info.border,
    iconBackground: (theme) => theme.palette.intent?.info.border,
    color: (theme) => theme.palette.intent?.info.text,
  },
};

export function InterviewTypeCalloutCard(props: InterviewTypeCalloutCardProps) {
  const { placement } = props;

  const variant = getInterviewVariant(placement);
  const config = VARIANT_CONFIGS[variant];

  return (
    <Box
      sx={{
        backgroundColor: config.backgroundColor,
        border: "1.5px solid",
        borderColor: config.borderColor,
        borderRadius: 2,
        px: 4,
        py: 4,
        display: "flex",
        gap: 3,
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          backgroundColor: config.iconBackground,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <CbhIcon type={config.iconType} size="small" color={config.color} />
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
        <Text semibold variant="body1" sx={{ color: config.color }}>
          {config.title}
        </Text>
        <Text variant="body2" sx={{ color: config.color }}>
          {config.description}
        </Text>
      </Box>
    </Box>
  );
}
